import { FC, useMemo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint, useToasts } from "@brg/ui";
import { useFormContext } from "react-hook-form";
import { useTheme } from "styled-components";
import { API } from "@brg/api";

import { AuthorT, ProjectFormObject } from "../../types";
import DesignerSelect from "./DesignerSelect";
import { bProfile } from "core/utils/bProfile";
import { useProfile } from "core/hooks/auth/useProfile";
import { ProjectAuthorsChangeCbT } from "../../ProjectAuthorsEdit";
import { MAX_AUTHORS_COUNT } from "../../constants";
import ProjectAuthorsDisplay from "../../ProjectAuthorsDisplay";
import AuthorField from "./AuthorField";
import { ProjectEditPageGridRow, ProjectEditPageGridLabel } from "../../styles";
import { LabelRedStar } from "../../../designerEditView/styles";
import { useRouteMatch } from "react-router-dom";

type AuthorsSelectProps = {
  isPresentation?: boolean;
};

  const AuthorsSelect: FC<AuthorsSelectProps> = ({ isPresentation }) => {
    const { t, i18n } = useTranslation();
  const profile = useProfile();
  const toasts = useToasts();
  const { getValues, setValue, watch } = useFormContext<ProjectFormObject>();
  const is834 = useBreakpoint(834);
  const theme = useTheme();
  const [localAuthors, setLocalAuthors] = useState<AuthorT[]>([]);
  const watchedAuthors = watch('authors') || [];
  const authors = localAuthors.length > 0 ? localAuthors : watchedAuthors;
  const coAuthors = watch('coAuthors') || [];
  const isProjectEditPage = !!useRouteMatch("/edit") || !!useRouteMatch("/books/edit");
  
  const processedAuthors = useMemo(() => {
    if (authors && authors.length > 0) {
      return authors.map(author => API.toLang(author, i18n.language));
    }
    return authors;
  }, [authors, i18n.language]);
  
  const formObject = { authors: processedAuthors, coAuthors };
  const project = formObject;

  useEffect(() => {
    if (watchedAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(watchedAuthors as any);
    }
  }, [watchedAuthors, localAuthors.length]);

  useEffect(() => {
    const formAuthors = getValues('authors');
    if (formAuthors && formAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(formAuthors as any);
    }
  }, [getValues, localAuthors.length]);

  const isOwnProject = useMemo(() => formObject.authors?.some(({ accountId }) => {
    return accountId === profile?.accountId
  }), [formObject.authors, profile])

  const shouldDisplayAuthorField = useMemo(
    () => (profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)) && !isPresentation
    ), [profile, isPresentation])

  const getTrueIfCoauthorIsAuthor = useCallback((authors: AuthorT[]) => {
    return false;
  }, [])

  const deleteUserIfExist = (arr: AuthorT[], arr1: AuthorT[], fn: Function, justOne: boolean) => {
    if (arr.length && arr1.length) {
      if (justOne) {
        return (arr.find((i) => i.designerId === arr1[0].designerId)) ? fn([]) : fn(arr1);
      } else {
        return fn(arr1.filter(i => i.designerId !== arr[0].designerId));
      }
    }
  }

      const onProjectAuthorsChange = useCallback(
        ({ authors, action }: ProjectAuthorsChangeCbT) => {
          if (action === 'select-option') {
            if (authors.length > 1) {
              toasts.error('Автор может быть только один!');
              return;
            }
          }

          setValue('authors', authors as any);
          setLocalAuthors(authors as any);
        },
        [toasts, setValue],
      );

  const onProjectCoAuthorsChange = useCallback(
    ({ authors, action, removedValue }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > MAX_AUTHORS_COUNT) {
          toasts.error(`Нельзя добавить больше ${MAX_AUTHORS_COUNT} соавторов!`);
          return;
        }
      } else if (action === 'remove-value' || action === 'pop-value') {
        if (!(removedValue?.accountId === profile?.accountId
          || bProfile.isModer(profile)
          || bProfile.isMiddleModer(profile)
          || isOwnProject)) {
          return;
        }
      }
      if (getTrueIfCoauthorIsAuthor(authors)) {
        toasts.error(t('toasts.project_coauthor_error'));
        return;
      }

      deleteUserIfExist(authors as any, formObject.authors as any, (val: any) => setValue('authors', val), true);

      setValue('coAuthors', authors as any);
    },
    [formObject.authors, formObject.coAuthors, t, profile, toasts, isOwnProject, getTrueIfCoauthorIsAuthor],
  );

  return (
    <>
      {shouldDisplayAuthorField && (
        <ProjectEditPageGridRow isProjectEdit={isProjectEditPage}>
          <ProjectEditPageGridLabel>
            {t('project_edit.author')}
            <LabelRedStar>*</LabelRedStar>
          </ProjectEditPageGridLabel>
          {formObject.authors && formObject.authors.length > 0 ? (
            <ProjectAuthorsDisplay
              authors={(formObject.authors || []) as any}
              editing={false}
              onAddClick={() => {}}
              onRemoveClick={(authors: AuthorT[], { action, removedValue }: { action: string, removedValue: AuthorT }) => {
                onProjectAuthorsChange({ authors, action: 'remove-value' });
              }}
              isProjectEdit={true}
              canRemoveUser={true}
            />
          ) : (
            <AuthorField
              value={(formObject.authors || []) as any}
              onChange={(authors: AuthorT[]) => {
                if (authors.length > 0) {
                  onProjectAuthorsChange({ authors, action: 'select-option' });
                } else {
                  onProjectAuthorsChange({ authors, action: 'remove-value' });
                }
              }}
              required
              placeholder={t('search_by_name')}
            />
          )}
        </ProjectEditPageGridRow>
      )}
      <DesignerSelect
        name="coAuthors"
        label={t('project_edit.coauthors')}
        ignoreContext={true}
        onChange={onProjectCoAuthorsChange}
        placeholder={t('search_by_name')}
        persist={is834}
        isPresentation={isPresentation}
        isProjectEdit
        customStyles={{
          backgroundColor: theme.projectEdit.inputs.bg,
          color: theme.projectEdit.inputs.color,
          placeholder: theme.projectEdit.inputs.placeholder,
        }}
      />
    </>
  );
};

export default AuthorsSelect;
import { FC, useMemo, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBreakpoint, useToasts } from "@brg/ui";
import { useFormContext } from "react-hook-form";
import { useTheme } from "styled-components";
import { API } from "@brg/api";

import { AuthorT, ProjectFormObject } from "../../types";
import DesignerSelect from "./DesignerSelect";
import { bProfile } from "core/utils/bProfile";
import { useProfile } from "core/hooks/auth/useProfile";
import { ProjectAuthorsChangeCbT } from "../../ProjectAuthorsEdit";
import { MAX_AUTHORS_COUNT } from "../../constants";
import ProjectAuthorsDisplay from "../../ProjectAuthorsDisplay";
import AuthorField from "./AuthorField";
import { ProjectEditPageGridRow, ProjectEditPageGridLabel } from "../../styles";
import { LabelRedStar } from "../../../designerEditView/styles";
import { useRouteMatch } from "react-router-dom";

type AuthorsSelectProps = {
  isPresentation?: boolean;
};

  const AuthorsSelect: FC<AuthorsSelectProps> = ({ isPresentation }) => {
    const { t, i18n } = useTranslation();
  const profile = useProfile();
  const toasts = useToasts();
  const { getValues, setValue, watch } = useFormContext<ProjectFormObject>();
  const is834 = useBreakpoint(834);
  const theme = useTheme();
  const [localAuthors, setLocalAuthors] = useState<AuthorT[]>([]);
  const watchedAuthors = watch('authors') || [];
  const authors = localAuthors.length > 0 ? localAuthors : watchedAuthors;
  const coAuthors = watch('coAuthors') || [];
  const isProjectEditPage = !!useRouteMatch("/edit") || !!useRouteMatch("/books/edit");
  
  const processedAuthors = useMemo(() => {
    if (authors && authors.length > 0) {
      return authors.map(author => API.toLang(author, i18n.language));
    }
    return authors;
  }, [authors, i18n.language]);
  
  const formObject = { authors: processedAuthors, coAuthors };
  const project = formObject;

  useEffect(() => {
    if (watchedAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(watchedAuthors as any);
    }
  }, [watchedAuthors, localAuthors.length]);

  useEffect(() => {
    const formAuthors = getValues('authors');
    if (formAuthors && formAuthors.length > 0 && localAuthors.length === 0) {
      setLocalAuthors(formAuthors as any);
    }
  }, [getValues, localAuthors.length]);

  const isOwnProject = useMemo(() => formObject.authors?.some(({ accountId }) => {
    return accountId === profile?.accountId
  }), [formObject.authors, profile])

  const shouldDisplayAuthorField = useMemo(
    () => (profile && (bProfile.isModer(profile) || bProfile.isMiddleModer(profile)) && !isPresentation
    ), [profile, isPresentation])

  const getTrueIfCoauthorIsAuthor = useCallback((authors: AuthorT[]) => {
    return false;
  }, [])

  const deleteUserIfExist = (arr: AuthorT[], arr1: AuthorT[], fn: Function, justOne: boolean) => {
    if (arr.length && arr1.length) {
      if (justOne) {
        return (arr.find((i) => i.designerId === arr1[0].designerId)) ? fn([]) : fn(arr1);
      } else {
        return fn(arr1.filter(i => i.designerId !== arr[0].designerId));
      }
    }
  }

      const onProjectAuthorsChange = useCallback(
        ({ authors, action }: ProjectAuthorsChangeCbT) => {
          if (action === 'select-option') {
            if (authors.length > 1) {
              toasts.error('Автор может быть только один!');
              return;
            }
          }

          setValue('authors', authors as any);
          setLocalAuthors(authors as any);
        },
        [toasts, setValue],
      );

  const onProjectCoAuthorsChange = useCallback(
    ({ authors, action, removedValue }: ProjectAuthorsChangeCbT) => {
      if (action === 'select-option') {
        if (authors.length > MAX_AUTHORS_COUNT) {
          toasts.error(`Нельзя добавить больше ${MAX_AUTHORS_COUNT} соавторов!`);
          return;
        }
      } else if (action === 'remove-value' || action === 'pop-value') {
        if (!(removedValue?.accountId === profile?.accountId
          || bProfile.isModer(profile)
          || bProfile.isMiddleModer(profile)
          || isOwnProject)) {
          return;
        }
      }
      if (getTrueIfCoauthorIsAuthor(authors)) {
        toasts.error(t('toasts.project_coauthor_error'));
        return;
      }

      deleteUserIfExist(authors as any, formObject.authors as any, (val: any) => setValue('authors', val), true);

      setValue('coAuthors', authors as any);
    },
    [formObject.authors, formObject.coAuthors, t, profile, toasts, isOwnProject, getTrueIfCoauthorIsAuthor],
  );

  return (
    <>
      {shouldDisplayAuthorField && (
        <ProjectEditPageGridRow isProjectEdit={isProjectEditPage}>
          <ProjectEditPageGridLabel>
            {t('project_edit.author')}
            <LabelRedStar>*</LabelRedStar>
          </ProjectEditPageGridLabel>
          {formObject.authors && formObject.authors.length > 0 ? (
            <ProjectAuthorsDisplay
              authors={(formObject.authors || []) as any}
              editing={false}
              onAddClick={() => {}}
              onRemoveClick={(authors: AuthorT[], { action, removedValue }: { action: string, removedValue: AuthorT }) => {
                onProjectAuthorsChange({ authors, action: 'remove-value' });
              }}
              isProjectEdit={true}
              canRemoveUser={true}
            />
          ) : (
            <AuthorField
              value={(formObject.authors || []) as any}
              onChange={(authors: AuthorT[]) => {
                if (authors.length > 0) {
                  onProjectAuthorsChange({ authors, action: 'select-option' });
                } else {
                  onProjectAuthorsChange({ authors, action: 'remove-value' });
                }
              }}
              required
              placeholder={t('search_by_name')}
            />
          )}
        </ProjectEditPageGridRow>
      )}
      <DesignerSelect
        name="coAuthors"
        label={t('project_edit.coauthors')}
        ignoreContext={true}
        onChange={onProjectCoAuthorsChange}
        placeholder={t('search_by_name')}
        persist={is834}
        isPresentation={isPresentation}
        isProjectEdit
        customStyles={{
          backgroundColor: theme.projectEdit.inputs.bg,
          color: theme.projectEdit.inputs.color,
          placeholder: theme.projectEdit.inputs.placeholder,
        }}
      />
    </>
  );
};

export default AuthorsSelect;
